import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

/**
 * Orders proficiency levels best → worst: lowest sortOrder first, so sortOrder 0 (the top level,
 * e.g. "Expert") sits at the start. The resulting index (0 = best … N-1 = worst) matches the
 * new storage/display order used across the admin UI and manager assessment.
 */
export const orderProficiencyLevels = <Level extends Pick<ProficiencyLevel, 'sortOrder'>>(
    levels: Level[],
): Level[] => [...levels].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

/**
 * Orders proficiency levels worst → best, the direction a reader walks a scale: the lowest level
 * first, the top one last. Every screen that *shows* the scale reads this one — the quick
 * assessment grid, the proficiency picker, the mastery legend — so they can never disagree.
 *
 * Ranking is a separate question and keeps {@link orderProficiencyLevels}: a level's rank decides
 * a score, and reversing the reading order must not move it.
 */
export const orderProficiencyLevelsWorstFirst = <Level extends Pick<ProficiencyLevel, 'sortOrder'>>(
    levels: Level[],
): Level[] => orderProficiencyLevels(levels).reverse();
